import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import IncidentStyle from '../styles/pages/CreateIncident.module.scss'
import { RootState } from '../store/GlobalStore'
import {
    IncidentFormData,
    IncidentResponse,
    MediaUploadResponse
} from '../utils/Interface'
import { Link, useNavigate } from 'react-router-dom'
import { useUploadMutation } from '../services/MediaAPI'
import PageHeader from '../components/layout/PageHeader'
import { FormData } from 'formdata-node'
import { useCreateIncidentMutation } from '../services/IncidentAPI'
import { addNotification } from '../store/slices/notificationSlice'
import { useTranslation } from 'react-i18next';

const IncidentPage: React.FC = () => {
    const { t, i18n  } = useTranslation();
    const authState = useSelector((state: RootState) => state.Auth)

    const reservations = authState.reservations;
    const activeReservation = reservations && reservations.length > 0 ? reservations[0] : null;

    const { register, handleSubmit, setValue, watch } =
        useForm<IncidentFormData>()
    const [submitted, setSubmitted] = useState(false)
    const [uploadMedia] = useUploadMutation()
    const [inputContainerClass, setInputContainerClass] = useState(
        IncidentStyle.InpuContainer
    )
    const [createIncident, { isLoading, isError, isSuccess }] = useCreateIncidentMutation()
    const [image, setImage] = useState<string | null>(null)
    const [titleFocused, setTitleFocused] = useState(false)
    const [descriptionFocused, setDescriptionFocused] = useState(false)
    const [categoryFocused, setCategoryFocused] = useState(false)
    const [hebergementFocused, setHebergementFocused] = useState(false)
    const title = watch('title')
    const category = watch('category')
    const description = watch('description')
    const hebergement = watch('hebergement')
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    const handleFileChange2 = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === "string") {
                    setImagePreview(reader.result);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        const file = e.target.files?.[0]
        if (file) {
            const formData = new FormData()
            formData.append('files', file)
            
            const media: { data: MediaUploadResponse[] } = (await uploadMedia(
                formData
            )) as { data: MediaUploadResponse[] }

            if (media?.data?.[0]) {
                setValue('photo', Number(media.data[0].id))
                setImage(
                    `${String(process.env.REACT_APP_BASE_URL)}${String(
                        media.data[0].url
                    )}`
                )
            }
        }
    }

    
    // - UseEffect
    useEffect(() => {
        if (submitted && isError) {
            setInputContainerClass(IncidentStyle.Rejected)
        } else {
            setInputContainerClass('')
        }
    }, [submitted, isError])

    useEffect(() => {
        if (!authState.user) {
            navigate('/')
        }
        if (authState.user?.id) setValue('user', authState.user.id)
    }, [authState.user])

    useEffect(() => {
        if (activeReservation?.Bookings) {
            const nonCancelled = activeReservation.Bookings.filter((b: any) => !b.cancelled)
            if (nonCancelled.length === 1) {
                const b = nonCancelled[0]
                setValue('hebergement', `${b.ressourceLabel} n\u00b0${b.ressourceNumber}`)
                setValue('hebergementBookingId', b.bookingId)
            }
        }
    }, [activeReservation])

  

    const onSubmit = async (formData: IncidentFormData) => {
        setSubmitted(true)
        try {
            const payload = {
                ...formData,
                reservation: {
                    connect: [{ id: (activeReservation as any)?.id }]
                },
            };
            const result = await createIncident(payload as any);
            
            if (result.data?.id) {
                dispatch(addNotification({ 
                    message: "L'incident a été créé et lié à la réservation.", 
                    type: 'success' 
                }));
                navigate('/mon-compte');
            }
        } catch (error) {
            console.error('Erreur API:', error);
        }
    }

    return (
        <>
        <PageHeader onBack={() => navigate(-1)} title={t('create_incident')} subtitle={t('create_incident_sub')} />
        <form
            className={IncidentStyle.ModifyLesseeForm}
            onSubmit={handleSubmit(onSubmit)}>
            <div
                className={`${IncidentStyle.ModifyLesseeFormChild} ${inputContainerClass}`}>
                <input type='hidden' {...register('user')} />
                <input type='hidden' {...register('hebergement')} />
                <input type='hidden' {...register('hebergementBookingId')} />
                <div className={IncidentStyle.DesktopFormField}>
                    <div
                        className={`${IncidentStyle.InputContainer} ${IncidentStyle.IncidentCategory} ${
                            categoryFocused || category ? IncidentStyle.Filled : ''
                        } ${isError && submitted ? IncidentStyle.Rejected : ''}`}>
                        <select
                            id='category'
                            {...register('category', { required: true })}
                            required
                            onFocus={() => setCategoryFocused(true)}
                            onBlur={() => setCategoryFocused(false)}
                        >
                            <option value="">{t('cat')}</option>
                            <option value="dysfonction">{t('dysfonction')}</option>
                            <option value="serviceRoom">{t('serviceRoom')}</option>
                            <option value="housingIncident">{t('housingIncident')}</option>

                            <option value="noSheet">{t('noSheet')}</option>
                            <option value="noElectricity">{t('noElectricity')}</option>
                            <option value="noWater">{t('noWater')}</option>
                            <option value="noLight">{t('noLight')}</option>
                            <option value="waterLeak">{t('waterLeak')}</option>
                            <option value="other">{t('other')}</option>
                            </select>
                    </div>
                    <div
                        className={`${IncidentStyle.InputContainer} ${IncidentStyle.IncidentTitle} ${
                            titleFocused || title
                                ? IncidentStyle.Filled
                                : ''
                        } ${isError && submitted ? IncidentStyle.Rejected : ''}`}>
                        <input
                            type='text'
                            id='title'
                            {...register('title', { required: true })}
                            required
                            onFocus={() => setTitleFocused(true)}
                            onBlur={() => setTitleFocused(false)}
                            
                        />
                        <label htmlFor='title'>{t('incident_title')}</label>
                    </div>
                </div>
                <div className={IncidentStyle.DesktopFormField}>
                    {activeReservation?.Bookings && activeReservation.Bookings.filter((b: any) => !b.cancelled).length > 0 && (
                        <div
                            className={`${IncidentStyle.InputContainer} ${IncidentStyle.IncidentHebergement} ${
                                hebergementFocused || hebergement ? IncidentStyle.Filled : ''
                            }`}>
                            <select
                                id='hebergement-select'
                                defaultValue={activeReservation.Bookings.filter((b: any) => !b.cancelled).length === 1
                                    ? activeReservation.Bookings.filter((b: any) => !b.cancelled)[0].bookingId
                                    : ''}
                                onChange={(e) => {
                                    const selectedBookingId = Number(e.target.value)
                                    const booking = activeReservation.Bookings?.find((b: any) => b.bookingId === selectedBookingId)
                                    if (booking) {
                                        setValue('hebergement', `${booking.ressourceLabel} n\u00b0${booking.ressourceNumber}`)
                                        setValue('hebergementBookingId', booking.bookingId)
                                    } else {
                                        setValue('hebergement', undefined as any)
                                        setValue('hebergementBookingId', undefined as any)
                                    }
                                }}
                                onFocus={() => setHebergementFocused(true)}
                                onBlur={() => setHebergementFocused(false)}
                            >
                                <option value="">{t('hebergement_concern')}</option>
                                {activeReservation.Bookings.filter((b: any) => !b.cancelled).map((b: any) => (
                                    <option key={b.bookingId} value={b.bookingId}>
                                        {b.ressourceLabel}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div
                        className={`${IncidentStyle.TextAreaContainer} ${
                            descriptionFocused || description ? IncidentStyle.Filled : ''
                        } ${isError && submitted ? IncidentStyle.Rejected : ''}`}>
                        <textarea
                            id='description'
                            {...register('description', { required: true })}
                            required
                            onFocus={() => setDescriptionFocused(true)}
                            onBlur={() => setDescriptionFocused(false)}
                        />
                        <label htmlFor='description'>{t('incident_precision')}</label>
                    </div>
                </div>
                <div className={IncidentStyle.imageUploadContainer}>
                {t('photo')}(s)
                </div>
                <label 
                htmlFor="file-input" 
                className={IncidentStyle.InputLabel}
                >
                    <span className={IncidentStyle.cross}>+</span>
                    <span className={IncidentStyle.ajouter}>{t('add_photo')}</span>
                </label>
                <input
                    id='file-input'
                    type='file'
                    className={IncidentStyle.hidden}
                    onChange={handleFileChange2}
                />
                <input
                    type='hidden'
                    {...register('photo')} // Enregistrer profilImage
                />
                {/* Aperçu de l'image */}
            {imagePreview && (
                <div className={IncidentStyle.imageContainer}>
                    <img 
                        src={imagePreview} 
                        alt="Aperçu" 
                        className={IncidentStyle.imageInput}
                    />
                    <button
                        type="button"
                        className={IncidentStyle.deleteButton}
                        onClick={() => setImagePreview(null)} // Clear the preview
                    >
                        x
                        {/* x<img src={deleteIcon} alt="Delete" /> */}
                    </button>
                    
                </div>
            )}
            <div className={IncidentStyle.buttonContainer}>
                {/*<Link to='/mon-compte'>
                    <button className={IncidentStyle.Cancel}>Annuler</button>
                </Link>*/}
                <button
                    type='submit'
                    disabled={isLoading}
                    className={IncidentStyle.Update}>
                    {isLoading ? t('loading') + '...' : t('send')}
                </button>
            </div>
            </div>
            
            {isError && <p>Error occurred during update.</p>}
            {isSuccess && <p>Updatesuccessful!</p>}
        </form>
    </>
    )
}

export default IncidentPage
